import {
  DEFAULT_TEMPERATURE,
  DEFAULT_VOICE_TYPE,
} from "@/components/shared/evaluation/EvaluationConstants"
import {
  isWebSocketConnectionMode,
  SimulatorType,
  WebSocketConnectionMode,
} from "@/types/api/SimulatorType"
import type {
  ChatAgentMetadata,
  OutboundVoiceAgentMetadata,
  SimulationMetadata,
} from "@/types/backend-contracts"
import { isHttpMethod } from "@/utils/simulation/httpMethod"
import {
  GEMINI_REALTIME_DEFAULT_MODEL,
  GEMINI_REALTIME_DEFAULT_VOICE,
  GROK_REALTIME_DEFAULT_MODEL,
  GROK_REALTIME_DEFAULT_VOICE,
  OPENAI_REALTIME_API_KEY_SOURCE,
  SIMULATOR_FIELDS,
} from "@/utils/simulation/simulatorFields"

import type { ChatWebSocketSimulatorConfigData } from "../components/simulators/chat-websocket-config/types"
import type { WebSocketSimulatorConfigData } from "../components/simulators/websocket-config/types"

/**
 * Build a metadata object from the default_value entries in SIMULATOR_FIELDS
 * for a given simulator type. Resolves option_values mappings when present.
 */
function buildFieldDefaults(
  simulatorType: SimulatorType,
): Record<string, unknown> {
  const defaults: Record<string, unknown> = {}
  const fields = SIMULATOR_FIELDS[simulatorType] || []

  fields.forEach((field) => {
    if (field.default_value !== undefined) {
      if (field.option_values && field.default_value !== undefined) {
        defaults[field.key] =
          field.option_values[field.default_value] ?? field.default_value
      } else {
        defaults[field.key] = field.default_value
      }
    }
  })

  return defaults
}

type HttpMethod =
  | "GET"
  | "POST"
  | "PUT"
  | "PATCH"
  | "DELETE"
  | "HEAD"
  | "OPTIONS"
const DEFAULT_HTTP_METHOD: HttpMethod = "POST"
const EMPTY_JSON_OBJECT = "{}"
function skipsWebSocketReadyMessage(connectionMode: unknown): boolean {
  return (
    connectionMode === WebSocketConnectionMode.HTTP_FIRST ||
    connectionMode === WebSocketConnectionMode.TWIML_WEBHOOK
  )
}

function isChatWebSocketConnectionMode(
  connectionMode: unknown,
): connectionMode is
  | WebSocketConnectionMode.DIRECT
  | WebSocketConnectionMode.HTTP_FIRST {
  return (
    connectionMode === WebSocketConnectionMode.DIRECT ||
    connectionMode === WebSocketConnectionMode.HTTP_FIRST
  )
}

// A2A Chat defaults — these values are fixed by the ServiceNow A2A v2 JSON-RPC
// protocol spec (response envelope shape, status state machine), not
// customer-configurable. Kept as defaults so the create-agent form doesn't
// need to surface them.
const DEFAULT_A2A_RESPONSE_MESSAGE_PATH = "result.artifacts.0.parts.0.text"
const DEFAULT_A2A_RESPONSE_STATE_EXTRACTION: Record<string, string> = {
  contextId: "result.contextId",
  taskId: "result.id",
}
const DEFAULT_A2A_END_STATE_PATH = "result.status.state"
const DEFAULT_A2A_END_STATE_VALUES: readonly string[] = ["completed", "failed"]
const DEFAULT_A2A_INITIALIZATION_CONTENT_TYPE =
  "application/x-www-form-urlencoded"

// Chat WebSocket defaults
const DEFAULT_SEND_MESSAGE_TEMPLATE =
  '{"type": "message", "text": "{{message}}"}'
const DEFAULT_SEND_MEDIA_TEMPLATE = `{
  "type": "media",
  "name": "{{media_name}}",
  "mime_type": "{{mime_type}}",
  "data": "{{media_data}}"
}`
const DEFAULT_RECEIVE_TEXT_PATH = "text"
const DEFAULT_RECEIVE_TYPE_PATH = "type"
const DEFAULT_TEXT_MESSAGE_TYPE_VALUES = ["message"]
const DEFAULT_OUTBOUND_DIRECTION_VALUE = "Outbound"
const DEFAULT_HANDSHAKE_TIMEOUT_SECONDS = 30
const DEFAULT_COALESCE_TIMEOUT_SECONDS = 2.0
const DEFAULT_INITIAL_MESSAGE = "Hello!"

interface BaseSimulatorMetadata {
  [key: string]: unknown
}

interface VoiceSimulatorMetadata extends BaseSimulatorMetadata {
  phone_number?: string
  background_sound?: string
  voice_id?: string
  first_message?: string
  endpoint?: string
  agent_id?: string
  first_message_mode?: "assistant-speaks-first" | "assistant-waits-for-user"
}

interface OutboundVoiceSimulatorMetadata
  extends OutboundVoiceAgentMetadata, BaseSimulatorMetadata {
  background_sound?: string
  voice_id?: string
  first_message_mode?: "assistant-speaks-first" | "assistant-waits-for-user"
  phone_number_key?: string
}

interface STTSimulatorMetadata extends BaseSimulatorMetadata {
  provider?: string
  model?: string
  language?: string
}

interface TextSimulatorMetadata
  extends ChatAgentMetadata, BaseSimulatorMetadata {}

interface SMSSimulatorMetadata extends BaseSimulatorMetadata {
  phone_number?: string
}

interface ChatWebSocketSimulatorMetadata
  extends ChatWebSocketSimulatorConfigData, BaseSimulatorMetadata {
  connection_mode?: WebSocketConnectionMode
  http_method?: HttpMethod
}

interface WebSocketSimulatorMetadata
  extends WebSocketSimulatorConfigData, BaseSimulatorMetadata {
  connection_mode?: WebSocketConnectionMode
  http_method?: HttpMethod
}

interface A2AChatSimulatorMetadata extends BaseSimulatorMetadata {
  chat_endpoint?: string
  initialization_endpoint?: string
  initialization_payload?: string
  initialization_content_type?: string
  response_message_path?: string
  response_state_extraction?: Record<string, string>
  end_state_path?: string
  end_state_values?: readonly string[]
}

interface OpenAIRealtimeSimulatorMetadata extends BaseSimulatorMetadata {
  openai_realtime_api_key?: string
  openai_realtime_api_key_source?: (typeof OPENAI_REALTIME_API_KEY_SOURCE)[keyof typeof OPENAI_REALTIME_API_KEY_SOURCE]
  openai_realtime_instructions?: string
  openai_realtime_model?: string
  openai_realtime_voice?: string
  openai_realtime_temperature?: number
  openai_realtime_web_search_enabled?: boolean
  simulation_timeout_seconds?: number
}

interface GeminiRealtimeSimulatorMetadata extends BaseSimulatorMetadata {
  gemini_realtime_api_key?: string
  gemini_realtime_instructions?: string
  gemini_realtime_model?: string
  gemini_realtime_voice?: string
  gemini_realtime_temperature?: number
  simulation_timeout_seconds?: number
}

// NOTE: no temperature field — the Grok Voice Agent API does not expose a
// temperature knob in its session config.
interface GrokRealtimeSimulatorMetadata extends BaseSimulatorMetadata {
  grok_realtime_api_key?: string
  grok_realtime_instructions?: string
  grok_realtime_model?: string
  grok_realtime_voice?: string
  simulation_timeout_seconds?: number
}

// NOTE: no model/voice fields — Deepgram's Voice Agent API takes a full
// Settings message (listen/think/speak providers) instead of single knobs.
interface DeepgramRealtimeSimulatorMetadata extends BaseSimulatorMetadata {
  deepgram_realtime_api_key?: string
  deepgram_realtime_prompt?: string
  deepgram_realtime_functions?: unknown[] | string
  deepgram_realtime_settings?: Record<string, unknown> | string
  simulation_timeout_seconds?: number
}

type SimulatorMetadata =
  | VoiceSimulatorMetadata
  | OutboundVoiceSimulatorMetadata
  | STTSimulatorMetadata
  | TextSimulatorMetadata
  | SMSSimulatorMetadata
  | A2AChatSimulatorMetadata
  | OpenAIRealtimeSimulatorMetadata
  | GeminiRealtimeSimulatorMetadata
  | GrokRealtimeSimulatorMetadata
  | DeepgramRealtimeSimulatorMetadata
  | WebSocketSimulatorMetadata
  | ChatWebSocketSimulatorMetadata

export function getDefaultMetadataForSimulator(
  simulatorType: SimulatorType,
  existingMetadata?: SimulationMetadata,
): SimulatorMetadata {
  switch (simulatorType) {
    case SimulatorType.VOICE:
      return {
        background_sound: "off",
        language: "en",
        phone_number:
          (existingMetadata as VoiceSimulatorMetadata)?.phone_number || "",
        voice_id: DEFAULT_VOICE_TYPE,
        temperature: DEFAULT_TEMPERATURE,
        first_message_mode:
          (existingMetadata as VoiceSimulatorMetadata)?.first_message_mode ||
          "assistant-speaks-first",
      } as VoiceSimulatorMetadata

    case SimulatorType.OUTBOUND_VOICE:
      return {
        background_sound: "off",
        language: "en",
        voice_id: DEFAULT_VOICE_TYPE,
        first_message_mode:
          (existingMetadata as VoiceSimulatorMetadata)?.first_message_mode ||
          "assistant-speaks-first",
        temperature: DEFAULT_TEMPERATURE,
        trigger_call_endpoint: "",
        trigger_call_headers: "{}",
        trigger_call_payload: "{}",
        phone_number_key: undefined,
      } as OutboundVoiceSimulatorMetadata

    case SimulatorType.STT:
      return {
        provider: "openai",
        model: "whisper-1",
        language: "en",
      } as STTSimulatorMetadata

    case SimulatorType.CHAT_A2A:
      return {
        chat_endpoint: existingMetadata?.chat_endpoint ?? "",
        initialization_endpoint:
          existingMetadata?.initialization_endpoint ?? "",
        initialization_payload: existingMetadata?.initialization_payload ?? "",
        initialization_content_type:
          existingMetadata?.initialization_content_type ??
          DEFAULT_A2A_INITIALIZATION_CONTENT_TYPE,
        response_message_path:
          existingMetadata?.response_message_path ??
          DEFAULT_A2A_RESPONSE_MESSAGE_PATH,
        response_state_extraction:
          existingMetadata?.response_state_extraction ?? {
            ...DEFAULT_A2A_RESPONSE_STATE_EXTRACTION,
          },
        end_state_path:
          existingMetadata?.end_state_path ?? DEFAULT_A2A_END_STATE_PATH,
        end_state_values: existingMetadata?.end_state_values ?? [
          ...DEFAULT_A2A_END_STATE_VALUES,
        ],
      } as A2AChatSimulatorMetadata

    case SimulatorType.TEXT:
      return buildFieldDefaults(SimulatorType.TEXT) as TextSimulatorMetadata

    case SimulatorType.EMAIL:
      return {
        recipient_email: existingMetadata?.recipient_email ?? "",
        subject: existingMetadata?.subject ?? "",
      }

    case SimulatorType.SMS:
      return {
        phone_number: "",
      } as SMSSimulatorMetadata

    case SimulatorType.OPENAI_REALTIME:
      return {
        openai_realtime_api_key_source:
          OPENAI_REALTIME_API_KEY_SOURCE.COVAL_MANAGED,
        openai_realtime_model: "gpt-realtime-2",
        openai_realtime_voice: "alloy",
        openai_realtime_temperature: 0.8,
        openai_realtime_web_search_enabled: false,
        simulation_timeout_seconds: 900,
      } as OpenAIRealtimeSimulatorMetadata

    case SimulatorType.GEMINI_REALTIME:
      return {
        gemini_realtime_api_key: "",
        gemini_realtime_model: GEMINI_REALTIME_DEFAULT_MODEL,
        gemini_realtime_voice: GEMINI_REALTIME_DEFAULT_VOICE,
        gemini_realtime_temperature: 0.8,
        simulation_timeout_seconds: 900,
      } as GeminiRealtimeSimulatorMetadata

    case SimulatorType.GROK_REALTIME:
      return {
        grok_realtime_api_key: "",
        grok_realtime_model: GROK_REALTIME_DEFAULT_MODEL,
        grok_realtime_voice: GROK_REALTIME_DEFAULT_VOICE,
        simulation_timeout_seconds: 900,
      } as GrokRealtimeSimulatorMetadata

    case SimulatorType.DEEPGRAM_REALTIME:
      return {
        deepgram_realtime_api_key: "",
        simulation_timeout_seconds: 900,
      } as DeepgramRealtimeSimulatorMetadata

    case SimulatorType.WEBSOCKET: {
      const connectionMode = isWebSocketConnectionMode(
        existingMetadata?.connection_mode,
      )
        ? existingMetadata.connection_mode
        : WebSocketConnectionMode.DIRECT
      return {
        endpoint: existingMetadata?.endpoint ?? "",
        connection_mode: connectionMode,
        http_url: existingMetadata?.http_url ?? "",
        http_method: isHttpMethod(existingMetadata?.http_method)
          ? existingMetadata.http_method
          : DEFAULT_HTTP_METHOD,
        http_request_body:
          existingMetadata?.http_request_body ?? EMPTY_JSON_OBJECT,
        http_headers: existingMetadata?.http_headers ?? EMPTY_JSON_OBJECT,
        websocket_url_response_path:
          existingMetadata?.websocket_url_response_path ?? "",
        ...(connectionMode === WebSocketConnectionMode.TWIML_WEBHOOK
          ? {
              voice_url: existingMetadata?.voice_url ?? "",
              voice_http_method:
                existingMetadata?.voice_http_method ?? DEFAULT_HTTP_METHOD,
              voice_http_headers:
                existingMetadata?.voice_http_headers ?? EMPTY_JSON_OBJECT,
              voice_form_fields:
                existingMetadata?.voice_form_fields ?? EMPTY_JSON_OBJECT,
              twilio_account_sid: existingMetadata?.twilio_account_sid ?? "",
              twilio_from_number: existingMetadata?.twilio_from_number ?? "",
              twilio_to_number: existingMetadata?.twilio_to_number ?? "",
            }
          : {}),
        handshake_ready_message_type:
          existingMetadata?.handshake_ready_message_type ??
          (skipsWebSocketReadyMessage(connectionMode) ? "" : "session_ready"),
        handshake_requires_session_id:
          existingMetadata?.handshake_requires_session_id ??
          !skipsWebSocketReadyMessage(connectionMode),
        send_media_template:
          existingMetadata?.send_media_template ?? DEFAULT_SEND_MEDIA_TEMPLATE,
      } as WebSocketSimulatorMetadata
    }

    case SimulatorType.CHAT_WEBSOCKET: {
      const connectionMode = isChatWebSocketConnectionMode(
        existingMetadata?.connection_mode,
      )
        ? existingMetadata.connection_mode
        : WebSocketConnectionMode.DIRECT
      return {
        endpoint: existingMetadata?.endpoint ?? "",
        connection_mode: connectionMode,
        http_url: existingMetadata?.http_url ?? "",
        http_method: isHttpMethod(existingMetadata?.http_method)
          ? existingMetadata.http_method
          : DEFAULT_HTTP_METHOD,
        http_request_body:
          existingMetadata?.http_request_body ?? EMPTY_JSON_OBJECT,
        http_headers: existingMetadata?.http_headers ?? EMPTY_JSON_OBJECT,
        websocket_url_response_path:
          existingMetadata?.websocket_url_response_path ?? "",
        initialization_json:
          existingMetadata?.initialization_json ?? EMPTY_JSON_OBJECT,
        authorization_header: existingMetadata?.authorization_header ?? "",
        custom_headers: existingMetadata?.custom_headers ?? EMPTY_JSON_OBJECT,
        send_message_template:
          existingMetadata?.send_message_template ??
          DEFAULT_SEND_MESSAGE_TEMPLATE,
        receive_message_text_path:
          existingMetadata?.receive_message_text_path ??
          DEFAULT_RECEIVE_TEXT_PATH,
        receive_message_type_path:
          existingMetadata?.receive_message_type_path ??
          DEFAULT_RECEIVE_TYPE_PATH,
        text_message_type_values:
          existingMetadata?.text_message_type_values ??
          DEFAULT_TEXT_MESSAGE_TYPE_VALUES,
        agent_disconnect_message_type:
          existingMetadata?.agent_disconnect_message_type ?? "",
        receive_message_direction_path:
          existingMetadata?.receive_message_direction_path ?? "",
        outbound_direction_value:
          existingMetadata?.outbound_direction_value ??
          DEFAULT_OUTBOUND_DIRECTION_VALUE,
        handshake_ready_message_type:
          existingMetadata?.handshake_ready_message_type ?? "",
        handshake_timeout_seconds:
          existingMetadata?.handshake_timeout_seconds ??
          DEFAULT_HANDSHAKE_TIMEOUT_SECONDS,
        message_coalesce_timeout_seconds:
          existingMetadata?.message_coalesce_timeout_seconds ??
          DEFAULT_COALESCE_TIMEOUT_SECONDS,
        initial_message:
          existingMetadata?.initial_message ?? DEFAULT_INITIAL_MESSAGE,
      } as ChatWebSocketSimulatorMetadata
    }

    case SimulatorType.SDK_WEBSOCKET:
    case SimulatorType.LIVEKIT:
    case SimulatorType.PIPECAT:
    case SimulatorType.NOOP:
    case SimulatorType.REALTIME_VOICE_TO_VOICE:
    default:
      return {}
  }
}
